import { ApolloClient } from 'apollo-client';
import { HttpLink } from 'apollo-link-http';
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { ApolloLink, from } from 'apollo-link';
import { setContext } from 'apollo-link-context';
import omitDeep from 'omit-deep-lodash';
import auth from '~lib/common/providers/auth';
import introspectionQueryResultData from '~lib/common/casesServiceFragmentTypes.json';

auth.renewSession();

var fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: introspectionQueryResultData,
});

function getAuthResult() {
  try {
    var raw = localStorage.getItem('vrpatients::auth::result');
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function tokenHasExpired(authResultParam) {
  return Date.now() > new Date(authResultParam.idTokenPayload.exp * 1000);
}

// Redirect to login page if user is not authenticated
if (
  window.location.pathname.indexOf('/authoring') === 0 &&
  window.location.pathname.indexOf('/authoring/auth/') !== 0
) {
  var authResultForRedirect = getAuthResult();

  if (!authResultForRedirect || tokenHasExpired(authResultForRedirect)) {
    window.location.replace('/auth/login?updated=' + Date.now()); // prevent caching
  } else if (
    authResultForRedirect &&
    authResultForRedirect.idTokenPayload &&
    authResultForRedirect.idTokenPayload.email_verified === false
  ) {
    window.location.replace('/auth/verify?updated=' + Date.now()); // prevent caching
  }
}

var cleanTypeNameLink = new ApolloLink(function (operation, forward) {
  if (operation.variables) {
    operation.variables = omitDeep(operation.variables, '__typename');
  }
  return forward(operation).map(function (data) {
    return data;
  });
});

/**
 * Dynamic URI per request, because orgGroups come from localStorage and may
 * be populated after login / group selection.
 */
var dynamicUriLink = new ApolloLink(function (operation, forward) {
  var base = process.env.VUE_APP_CASES_SERVICE_GRAPHQL_ENDPOINT;
  var orgGroups = auth.getOrgGroupParams();
  operation.setContext(function (prevContext) {
    // Honor an explicit per-operation uri (e.g. CaseCopyTool's
    // from/to environment targeting); fall back to the configured base.
    var resolvedBase = (prevContext && prevContext.uri) || base;
    var sep = resolvedBase.indexOf('?') === -1 ? '?' : '&';
    return {
      uri: resolvedBase + sep + 'orgGroups=' + orgGroups,
    };
  });
  return forward(operation);
});

/**
 * Dynamic Authorization per request, because authResult may not exist at module
 * init time, and can change after parseHash / renewSession.
 *
 * Keeping idToken as requested.
 */
var authLink = setContext(function (_, context) {
  var headers = (context && context.headers) ? context.headers : {};
  var authResult = getAuthResult();
  var idToken = authResult && authResult.idToken;

  var merged = {};
  // shallow copy headers (no object spread)
  for (var k in headers) {
    if (Object.prototype.hasOwnProperty.call(headers, k)) merged[k] = headers[k];
  }
  if (idToken) {
    merged.Authorization = 'Bearer ' + idToken;
  }

  return { headers: merged };
});

var httpLink = new HttpLink({
  // Base URI; dynamicUriLink will override per operation
  uri: process.env.VUE_APP_CASES_SERVICE_GRAPHQL_ENDPOINT,
  credentials: 'same-origin',
});

var link = from([
  cleanTypeNameLink,
  dynamicUriLink,
  authLink,
  httpLink,
]);

var apolloClient = new ApolloClient({
  link: link,
  cache: new InMemoryCache({ fragmentMatcher: fragmentMatcher }),
  connectToDevTools: true,
});

export default apolloClient;
